import { Link } from 'react-router-dom';

const NetworkPage = () => {
  return (
    <div data-testid="network-page">
      {/* Opening */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
              Global Authority
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              SAFE is not built by a single organisation.
            </p>
            <p className="text-lg md:text-xl text-slate-300">
              It is structured through independent functions that define, verify, implement, and apply the standard.
            </p>
          </div>
        </div>
      </section>

      {/* Core Principle */}
      <section className="py-16 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-xl md:text-2xl font-semibold tracking-tight text-white mb-4">
            No single entity can define, implement, and verify its own safety.
          </p>
          <p className="text-lg text-slate-300 mb-2">Each function operates independently.</p>
          <p className="text-lg text-white font-semibold">This separation preserves integrity.</p>
        </div>
      </section>

      {/* System Structure */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mb-12">
            <p className="caption text-cyan-700 mb-4">System Structure</p>
            <p className="text-lg text-slate-700">
              The SAFE ecosystem operates through distinct layers:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="bg-slate-900 text-white p-8 border-l-4 border-slate-900">
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Layer</p>
              <h3 className="text-lg font-semibold mb-2">Standard Definition</h3>
              <p className="text-slate-300">Defines what safety is</p>
            </div>
            <div className="bg-cyan-700 text-white p-8 border-l-4 border-cyan-700">
              <p className="text-xs uppercase tracking-widest text-cyan-200 mb-2">Layer</p>
              <h3 className="text-lg font-semibold mb-2">Independent Verification</h3>
              <p className="text-cyan-100">Determines whether systems meet the standard</p>
            </div>
            <div className="bg-amber-600 text-white p-8 border-l-4 border-amber-600">
              <p className="text-xs uppercase tracking-widest text-amber-200 mb-2">Layer</p>
              <h3 className="text-lg font-semibold mb-2">Implementation</h3>
              <p className="text-amber-100">Operationalises safety within systems</p>
            </div>
            <div className="bg-slate-800 text-white p-8 border-l-4 border-slate-600">
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Layer</p>
              <h3 className="text-lg font-semibold mb-2">Real-World Deployment</h3>
              <p className="text-slate-300">Applies the standard across environments and jurisdictions</p>
            </div>
          </div>

          <div className="mt-8 max-w-3xl">
            <p className="text-lg text-slate-900 font-semibold mb-2">Each layer is independent.</p>
            <p className="text-lg text-slate-900 font-semibold">No layer can replace another.</p>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Why This Matters</p>
            <p className="text-lg text-slate-700 mb-6">When roles are combined:</p>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Standards are influenced by implementation</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Verification loses independence</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Safety becomes self-declared</p>
              </div>
            </div>
            <p className="text-lg text-slate-900 font-semibold">
              Separation is required for credibility.
            </p>
          </div>
        </div>
      </section>

      {/* Global Application */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Global Application</p>
            <p className="text-lg text-slate-700 mb-6">SAFE is designed to operate across:</p>
            <div className="space-y-3 mb-8">
              <div className="border-l-4 border-cyan-700 pl-6">
                <p className="text-lg text-slate-900 font-medium">Jurisdictions</p>
              </div>
              <div className="border-l-4 border-cyan-700 pl-6">
                <p className="text-lg text-slate-900 font-medium">Industries</p>
              </div>
              <div className="border-l-4 border-cyan-700 pl-6">
                <p className="text-lg text-slate-900 font-medium">Regulatory environments</p>
              </div>
            </div>
            <p className="text-lg text-slate-700">
              It provides a consistent, independent reference point for what constitutes safety.
            </p>
          </div>
        </div>
      </section>

      {/* Position */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">Position</p>
            <p className="text-lg text-slate-300 mb-4">SAFE is not a network.</p>
            <p className="text-lg text-white font-semibold">
              It is a structured system of independent authority.
            </p>
          </div>
        </div>
      </section>

      {/* Final Line + CTA */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-xl md:text-2xl font-semibold tracking-tight text-slate-900 mb-12">
            Safety cannot be trusted<br />
            if it is defined, tested, and claimed by the same entity.
          </p>
          <Link
            to="/governance"
            className="btn-primary"
            data-testid="btn-view-standard"
          >
            View the Standard
          </Link>
        </div>
      </section>
    </div>
  );
};

export default NetworkPage;
